/**
 * SubmissionGuidance Component
 *
 * Displays jurisdiction-specific permit submission guidance including:
 * - Available submission methods (portal, email, in-person, mail)
 * - Portal URLs and instructions
 * - Payment information
 * - Pro tips and common mistakes
 *
 * Part of Phase 3: Submission Guidance & Review Tracking
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Globe,
  Mail,
  MapPin,
  Package,
  CreditCard,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
  Lightbulb,
  Clock,
  Phone,
  Copy,
  CheckCheck,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import type {
  SubmissionGuidance as SubmissionGuidanceType,
  SubmissionMethod,
} from '@/types/jurisdiction';

interface SubmissionGuidanceProps {
  guidance: SubmissionGuidanceType;
  jurisdictionName?: string;
  onOpenPortal?: () => void;
}

const methodIcons: Record<SubmissionMethod, typeof Globe> = {
  online_portal: Globe,
  email: Mail,
  in_person: MapPin,
  mail: Package,
};

const methodLabels: Record<SubmissionMethod, string> = {
  online_portal: 'Online Portal',
  email: 'Email',
  in_person: 'In Person',
  mail: 'Mail',
};

export function SubmissionGuidance({
  guidance,
  jurisdictionName,
  onOpenPortal,
}: SubmissionGuidanceProps) {
  const { toast } = useToast();
  const [copiedText, setCopiedText] = useState<string | null>(null);

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(text);
      toast({
        title: 'Copied!',
        description: `${label} copied to clipboard`,
      });
      setTimeout(() => setCopiedText(null), 2000);
    } catch (err) {
      toast({
        title: 'Failed to copy',
        description: 'Please copy manually',
        variant: 'destructive',
      });
    }
  };

  const CopyButton = ({ text, label }: { text: string; label: string }) => (
    <Button
      variant="ghost"
      size="icon"
      className="h-6 w-6"
      onClick={() => copyToClipboard(text, label)}
    >
      {copiedText === text ? (
        <CheckCheck className="h-3.5 w-3.5 text-green-600" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </Button>
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              How to Submit
            </CardTitle>
            <CardDescription>
              {jurisdictionName
                ? `Submission options for ${jurisdictionName}`
                : 'Available submission methods for your permit application'}
            </CardDescription>
          </div>
          <Badge variant="outline" className="shrink-0">
            {methodLabels[guidance.preferredMethod]} preferred
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Submission Methods Tabs */}
        <Tabs defaultValue={guidance.preferredMethod} className="w-full">
          <TabsList className="w-full grid" style={{ gridTemplateColumns: `repeat(${guidance.availableMethods.length}, 1fr)` }}>
            {guidance.availableMethods.map((method) => {
              const Icon = methodIcons[method];
              return (
                <TabsTrigger key={method} value={method} className="text-xs">
                  <Icon className="h-3.5 w-3.5 mr-1.5" />
                  {methodLabels[method]}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Online Portal Tab */}
          {guidance.portal && (
            <TabsContent value="online_portal" className="space-y-4 mt-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">Portal URL</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="text-xs bg-background px-2 py-1 rounded truncate max-w-[200px]">
                      {guidance.portal.url}
                    </code>
                    <CopyButton text={guidance.portal.url} label="Portal URL" />
                  </div>
                </div>

                <Button
                  className="w-full"
                  onClick={() => {
                    window.open(guidance.portal!.url, '_blank');
                    onOpenPortal?.();
                  }}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open Permit Portal
                </Button>

                {guidance.portal.loginRequired && (
                  <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg">
                    <AlertTriangle className="h-4 w-4 text-yellow-600 shrink-0 mt-0.5" />
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      Account registration required. Create your account before starting the application.
                    </p>
                  </div>
                )}

                {guidance.portal.acceptedFormats && guidance.portal.acceptedFormats.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Accepted File Formats
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {guidance.portal.acceptedFormats.map((format) => (
                        <Badge key={format} variant="secondary" className="text-xs">
                          {format.toUpperCase()}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {guidance.portal.maxFileSize && (
                  <p className="text-sm text-muted-foreground">
                    Maximum file size: <span className="font-medium">{guidance.portal.maxFileSize}</span>
                  </p>
                )}

                {guidance.portal.proTips && guidance.portal.proTips.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                      <Lightbulb className="h-3 w-3" />
                      Pro Tips
                    </p>
                    <ul className="space-y-1.5">
                      {guidance.portal.proTips.map((tip, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                          <span>{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {guidance.portal.commonMistakes && guidance.portal.commonMistakes.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Common Mistakes
                    </p>
                    <ul className="space-y-1.5">
                      {guidance.portal.commonMistakes.map((mistake, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <span className="text-red-500">•</span>
                          <span>{mistake}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </TabsContent>
          )}

          {/* Email Tab */}
          {guidance.email && (
            <TabsContent value="email" className="space-y-4 mt-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">Email Address</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={`mailto:${guidance.email.address}`}
                      className="text-sm text-primary hover:underline"
                    >
                      {guidance.email.address}
                    </a>
                    <CopyButton text={guidance.email.address} label="Email address" />
                  </div>
                </div>

                {guidance.email.subjectFormat && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">Subject Line Format</p>
                    <code className="block text-sm bg-muted p-2 rounded">
                      {guidance.email.subjectFormat}
                    </code>
                  </div>
                )}

                {guidance.email.attachmentNotes && (
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">Attachments:</span> {guidance.email.attachmentNotes}
                  </p>
                )}

                {guidance.email.responseTime && (
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span>Expected response: {guidance.email.responseTime}</span>
                  </div>
                )}
              </div>
            </TabsContent>
          )}

          {/* In Person Tab */}
          {guidance.inPerson && (
            <TabsContent value="in_person" className="space-y-4 mt-4">
              <div className="space-y-3">
                <div className="p-3 bg-muted rounded-lg space-y-2">
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Address</p>
                      <p className="text-sm text-muted-foreground">{guidance.inPerson.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <Clock className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">Hours</p>
                      <p className="text-sm text-muted-foreground">{guidance.inPerson.hours}</p>
                    </div>
                  </div>
                </div>

                {guidance.inPerson.appointmentRequired && (
                  <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-950/20 rounded-lg">
                    <AlertTriangle className="h-4 w-4 text-yellow-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                        Appointment Required
                      </p>
                      {guidance.inPerson.appointmentPhone && (
                        <p className="text-sm text-yellow-700 dark:text-yellow-300">
                          Call: <a href={`tel:${guidance.inPerson.appointmentPhone}`} className="underline">{guidance.inPerson.appointmentPhone}</a>
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {guidance.inPerson.whatToBring && guidance.inPerson.whatToBring.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      What to Bring
                    </p>
                    <ul className="space-y-1.5">
                      {guidance.inPerson.whatToBring.map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {guidance.inPerson.parking && (
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">Parking:</span> {guidance.inPerson.parking}
                  </p>
                )}
              </div>
            </TabsContent>
          )}

          {/* Mail Tab */}
          {guidance.mail && (
            <TabsContent value="mail" className="space-y-4 mt-4">
              <div className="space-y-3">
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Mailing Address</p>
                  {guidance.mail.attention && (
                    <p className="text-sm font-medium">{guidance.mail.attention}</p>
                  )}
                  <p className="text-sm whitespace-pre-line">{guidance.mail.address}</p>
                </div>

                {guidance.mail.includeWith && guidance.mail.includeWith.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Include With Submission
                    </p>
                    <ul className="space-y-1.5">
                      {guidance.mail.includeWith.map((item, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {guidance.mail.trackingRecommended && (
                  <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-950/20 rounded-lg">
                    <Lightbulb className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      Use certified mail with tracking for proof of submission
                    </p>
                  </div>
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>

        {/* Payment Information */}
        <div className="border-t pt-4 space-y-3">
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-primary" />
            <p className="text-sm font-medium">Payment Methods</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {guidance.payment.methods.map((method) => (
              <Badge key={method} variant="secondary" className="capitalize">
                {method.replace('_', ' ')}
              </Badge>
            ))}
          </div>
          {guidance.payment.checkPayableTo && (
            <p className="text-sm text-muted-foreground">
              Make checks payable to: <span className="font-medium">{guidance.payment.checkPayableTo}</span>
            </p>
          )}
          {guidance.payment.feeNotes && guidance.payment.feeNotes.length > 0 && (
            <div className="text-sm text-muted-foreground space-y-1">
              {guidance.payment.feeNotes.map((note, i) => (
                <p key={i}>{note}</p>
              ))}
            </div>
          )}
        </div>

        {/* General Tips */}
        {guidance.generalTips && guidance.generalTips.length > 0 && (
          <div className="border-t pt-4 space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1">
              <Lightbulb className="h-3 w-3" />
              General Tips
            </p>
            <ul className="space-y-1.5">
              {guidance.generalTips.map((tip, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <span className="text-primary">•</span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Deadline Notes */}
        {guidance.deadlineNotes && (
          <div className="flex items-start gap-2 p-3 bg-primary/5 rounded-lg">
            <Clock className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <p className="text-sm">{guidance.deadlineNotes}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default SubmissionGuidance;
